import { useState } from 'react';
import { Plus, Utensils } from 'lucide-react';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, EmptyState, Input, Skeleton, Textarea, useConfirm } from '@techsofit/ui';
import { FormShell } from '../components/FormShell';
import { ImageUpload } from '../components/ImageUpload';
import { useAsync } from '../lib/useAsync';
import { createRecipe, deleteRecipe, getRecipes, updateRecipe, type AdminRecipe } from '../lib/adminApi';

export function NutritionPage() {
  const { data, loading, error, reload } = useAsync(getRecipes);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const list = data ?? [];

  if (adding) {
    return (
      <FormShell title="New recipe" onBack={() => setAdding(false)}>
        <RecipeForm mode="create" onCancel={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />
      </FormShell>
    );
  }
  const editing = list.find((r) => r.id === editingId);
  if (editing) {
    return (
      <FormShell title="Edit recipe" onBack={() => setEditingId(null)}>
        <RecipeForm mode="edit" recipe={editing} onCancel={() => setEditingId(null)} onDone={() => { setEditingId(null); reload(); }} />
      </FormShell>
    );
  }

  return (
    <section className="mt-6 animate-fade-up">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-display-md text-ink">Nutrition</h1>
          <p className="mt-1 text-ink-soft">Recipes and meal ideas shown in the app.</p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus className="size-4" aria-hidden /> New recipe
        </Button>
      </div>

      {loading ? (
        <div className="mt-4 flex flex-col gap-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : error ? (
        <EmptyState title="Couldn't load recipes" description={error} />
      ) : list.length === 0 ? (
        <EmptyState title="No recipes yet" description="Add your first recipe for the app." />
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {list.map((r) => (
            <RecipeCard key={r.id} r={r} onEdit={() => setEditingId(r.id)} onDeleted={reload} />
          ))}
        </div>
      )}
    </section>
  );
}

function RecipeCard({ r, onEdit, onDeleted }: { r: AdminRecipe; onEdit: () => void; onDeleted: () => void }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  async function remove() {
    if (!(await confirm({ title: `Delete "${r.title}"?`, description: 'This cannot be undone.', confirmLabel: 'Delete', tone: 'danger' }))) return;
    setBusy(true);
    try {
      await deleteRecipe(r.id);
      onDeleted();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="flex items-center gap-3 p-4">
      <span className="grid size-10 shrink-0 place-items-center rounded-control bg-surface text-ink">
        <Utensils className="size-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-ink">{r.title}</p>
          {!r.published && <span className="rounded-pill bg-surface px-2 py-0.5 text-xs text-muted">Draft</span>}
          {r.featured && <span className="rounded-pill bg-accent-soft px-2 py-0.5 text-xs text-ink">Featured</span>}
        </div>
        <p className="text-xs text-muted">
          {r.category ?? '—'}
          {r.minutes != null ? ` · ${r.minutes} min` : ''}
          {r.calories != null ? ` · ${r.calories} kcal` : ''}
        </p>
      </div>
      <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
      <Button size="sm" variant="ghost" loading={busy} onClick={remove} className="text-bad-ink">Delete</Button>
    </Card>
  );
}

function RecipeForm({ mode, recipe, onCancel, onDone }: { mode: 'create' | 'edit'; recipe?: AdminRecipe; onCancel: () => void; onDone: () => void }) {
  const [slug, setSlug] = useState(recipe?.slug ?? '');
  const [title, setTitle] = useState(recipe?.title ?? '');
  const [category, setCategory] = useState(recipe?.category ?? '');
  const [minutes, setMinutes] = useState(recipe?.minutes != null ? String(recipe.minutes) : '');
  const [calories, setCalories] = useState(recipe?.calories != null ? String(recipe.calories) : '');
  const [protein, setProtein] = useState(recipe?.protein != null ? String(recipe.protein) : '');
  const [excerpt, setExcerpt] = useState(recipe?.excerpt ?? '');
  const [body, setBody] = useState(recipe?.body ?? '');
  const [coverImage, setCoverImage] = useState(recipe?.coverImage ?? '');
  const [published, setPublished] = useState(recipe?.published ?? false);
  const [featured, setFeatured] = useState(recipe?.featured ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const num = (s: string) => (s.trim() ? Number(s) : null);

  async function submit() {
    setError(null);
    if (!title.trim()) return setError('Give the recipe a title.');
    if (mode === 'create' && !slug.trim()) return setError('Give the recipe a slug (e.g. overnight-oats).');
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        category: category.trim() || null,
        minutes: num(minutes),
        calories: num(calories),
        protein: num(protein),
        excerpt: excerpt.trim() || null,
        body,
        coverImage: coverImage.trim() || null,
        published,
        featured,
      };
      if (mode === 'create') await createRecipe({ ...payload, slug: slug.trim() });
      else await updateRecipe(recipe!.id, payload);
      onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the recipe.');
      setSaving(false);
    }
  }

  return (
    <Card className="mb-1 flex flex-col gap-3 p-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Overnight oats" />
        {mode === 'create' && <Input label="Slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="overnight-oats" />}
        <Input label="Category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Breakfast" />
        <Input label="Minutes" type="number" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
        <Input label="Calories" type="number" inputMode="numeric" value={calories} onChange={(e) => setCalories(e.target.value)} />
        <Input label="Protein (g)" type="number" inputMode="numeric" value={protein} onChange={(e) => setProtein(e.target.value)} />
      </div>
      <ImageUpload value={coverImage || null} onChange={(url) => setCoverImage(url ?? '')} aspect={16 / 9} />
      <Input label="Excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="One-line summary" />
      <Textarea label="Body (ingredients + method)" value={body} onChange={(e) => setBody(e.target.value)} rows={10} />
      <div className="flex flex-wrap gap-5">
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} /> Published
        </label>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} /> Featured
        </label>
      </div>
      {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={submit} loading={saving}>Save</Button>
      </div>
    </Card>
  );
}
